import React, { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Filter,
  RefreshCw,
  RotateCcw,
  Server,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react';
import { AppShell } from '../components/layout/AppShell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  apiClient,
  type SecurityEventList,
  type SecurityEventOutcome,
  type SecurityEventSeverity,
  type SecurityIncidentDetail,
  type SecurityOverview,
} from '../lib/api-client';

const EVENT_PAGE_SIZE = 25;

interface EventFilterState {
  rangeSeconds: '3600' | '86400' | '604800' | '2592000';
  agentId: string;
  severity: '' | SecurityEventSeverity;
  outcome: '' | SecurityEventOutcome;
  eventType: string;
}

const DEFAULT_EVENT_FILTERS: EventFilterState = {
  rangeSeconds: '86400',
  agentId: '',
  severity: '',
  outcome: '',
  eventType: '',
};

function formatTime(epoch: number | null): string {
  if (!epoch) return 'Never';
  return new Date(epoch * 1_000).toLocaleString();
}

function humanizeEventType(eventType: string): string {
  const acronyms = new Set(['pam', 'ssh', 'ip']);
  return eventType
    .split('_')
    .filter(Boolean)
    .map((part) => acronyms.has(part) ? part.toUpperCase() : part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function severityClass(severity: string): string {
  if (severity === 'critical') return 'border-red-300 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/30 dark:text-red-100';
  if (severity === 'high') return 'border-orange-300 bg-orange-50 text-orange-900 dark:border-orange-900 dark:bg-orange-950/30 dark:text-orange-100';
  return 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100';
}

function severityRailClass(severity: SecurityEventSeverity): string {
  if (severity === 'critical') return 'border-l-red-500';
  if (severity === 'high') return 'border-l-orange-500';
  if (severity === 'warning') return 'border-l-amber-400';
  return 'border-l-blue-400';
}

function severityBadgeClass(severity: SecurityEventSeverity): string {
  if (severity === 'critical') return 'border-red-200 bg-red-100 text-red-800 dark:border-red-900 dark:bg-red-950/60 dark:text-red-200';
  if (severity === 'high') return 'border-orange-200 bg-orange-100 text-orange-800 dark:border-orange-900 dark:bg-orange-950/60 dark:text-orange-200';
  if (severity === 'warning') return 'border-amber-200 bg-amber-100 text-amber-800 dark:border-amber-900 dark:bg-amber-950/60 dark:text-amber-200';
  return 'border-blue-200 bg-blue-100 text-blue-800 dark:border-blue-900 dark:bg-blue-950/60 dark:text-blue-200';
}

function outcomeBadgeClass(outcome: SecurityEventOutcome): string {
  if (outcome === 'failure') return 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200';
  if (outcome === 'success') return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200';
  if (outcome === 'closed') return 'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200';
  return 'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950/30 dark:text-violet-200';
}

function incidentIdFromPath(): string | null {
  const match = window.location.pathname.match(/^\/security\/incidents\/([^/]+)$/);
  return match ? decodeURIComponent(match[1]) : null;
}

export const SecurityPage: React.FC = () => {
  const incidentId = useMemo(incidentIdFromPath, []);
  const [overview, setOverview] = useState<SecurityOverview | null>(null);
  const [incident, setIncident] = useState<SecurityIncidentDetail | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [eventFiltersDraft, setEventFiltersDraft] = useState<EventFilterState>({ ...DEFAULT_EVENT_FILTERS });
  const [eventFilters, setEventFilters] = useState<EventFilterState>({ ...DEFAULT_EVENT_FILTERS });
  const [eventPage, setEventPage] = useState(1);
  const [eventWindowTo, setEventWindowTo] = useState(() => Math.floor(Date.now() / 1_000));
  const [eventRefreshVersion, setEventRefreshVersion] = useState(0);
  const [eventList, setEventList] = useState<SecurityEventList | null>(null);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      if (incidentId) {
        setIncident(await apiClient.getSecurityIncident(incidentId));
      } else {
        setOverview(await apiClient.getSecurityOverview());
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Security monitoring could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [incidentId]);

  useEffect(() => {
    if (incidentId) return undefined;
    let active = true;

    const loadEvents = async () => {
      setEventsLoading(true);
      setEventsError('');
      try {
        const result = await apiClient.getSecurityEvents({
          page: eventPage,
          limit: EVENT_PAGE_SIZE,
          from: Math.max(0, eventWindowTo - Number(eventFilters.rangeSeconds)),
          to: eventWindowTo,
          agent_id: eventFilters.agentId || undefined,
          severity: eventFilters.severity || undefined,
          outcome: eventFilters.outcome || undefined,
          event_type: eventFilters.eventType || undefined,
        });
        if (active) setEventList(result);
      } catch (reason) {
        if (active) {
          setEventsError(reason instanceof Error ? reason.message : 'Security events could not be loaded.');
        }
      } finally {
        if (active) setEventsLoading(false);
      }
    };

    void loadEvents();
    return () => {
      active = false;
    };
  }, [
    incidentId,
    eventFilters,
    eventPage,
    eventWindowTo,
    eventRefreshVersion,
  ]);

  const refreshOverview = () => {
    void load();
    setEventWindowTo(Math.floor(Date.now() / 1_000));
    setEventRefreshVersion((version) => version + 1);
  };

  const applyEventFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setEventFilters({
      ...eventFiltersDraft,
      eventType: eventFiltersDraft.eventType.trim(),
    });
    setEventPage(1);
    setEventWindowTo(Math.floor(Date.now() / 1_000));
  };

  const resetEventFilters = () => {
    setEventFiltersDraft({ ...DEFAULT_EVENT_FILTERS });
    setEventFilters({ ...DEFAULT_EVENT_FILTERS });
    setEventPage(1);
    setEventWindowTo(Math.floor(Date.now() / 1_000));
  };

  if (incidentId) {
    return (
      <AppShell>
        <div className="mx-auto max-w-6xl space-y-6">
          <Link to="/security" className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline">
            <ArrowLeft className="h-4 w-4" />
            Security overview
          </Link>
          {loading && <p className="text-sm text-muted-foreground">Loading incident…</p>}
          {error && (
            <Card className="border-red-300">
              <CardContent className="flex items-center justify-between gap-4 pt-6">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                <Button variant="outline" onClick={() => void load()}>Retry</Button>
              </CardContent>
            </Card>
          )}
          {incident && (
            <>
              <div>
                <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
                  <span className={`rounded-full border px-2.5 py-1 font-medium ${severityClass(incident.incident.severity)}`}>
                    {incident.incident.severity}
                  </span>
                  <span className="rounded-full border px-2.5 py-1">{incident.incident.status}</span>
                </div>
                <h1 className="text-2xl font-bold">{incident.incident.title}</h1>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
                  {incident.incident.description}
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <MetricCard label="Evidence events" value={incident.incident.event_count} icon={<Activity className="h-5 w-5" />} />
                <MetricCard label="First seen" value={formatTime(incident.incident.first_seen_at)} icon={<ShieldAlert className="h-5 w-5" />} />
                <MetricCard label="Last seen" value={formatTime(incident.incident.last_seen_at)} icon={<RefreshCw className="h-5 w-5" />} />
              </div>
              <Card>
                <CardHeader>
                  <CardTitle>Normalized evidence</CardTitle>
                  <CardDescription>Bounded event metadata indexed for this tenant. Raw authentication lines are not exposed here.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {incident.evidence.length === 0 && <p className="text-sm text-muted-foreground">No indexed evidence is linked.</p>}
                    {incident.evidence.map((event) => (
                      <div key={event.id} className="rounded-xl border p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="font-medium">{event.event_type.split('_').join(' ')}</p>
                          <time className="text-xs text-muted-foreground">{formatTime(event.timestamp)}</time>
                        </div>
                        <p className="mt-2 text-sm text-muted-foreground">
                          {event.host} · {event.outcome}
                          {event.source_ip ? ` · ${event.source_ip}` : ''}
                          {event.user_name ? ` · user ${event.user_name}` : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </AppShell>
    );
  }

  const eventTotal = eventList?.pagination.total || 0;
  const eventStart = eventTotal === 0
    ? 0
    : ((eventList?.pagination.page || eventPage) - 1) * EVENT_PAGE_SIZE + 1;
  const eventEnd = Math.min(eventTotal, eventStart + (eventList?.events.length || 0) - 1);
  const hasActiveEventFilters = eventFilters.rangeSeconds !== DEFAULT_EVENT_FILTERS.rangeSeconds
    || Boolean(eventFilters.agentId)
    || Boolean(eventFilters.severity)
    || Boolean(eventFilters.outcome)
    || Boolean(eventFilters.eventType);

  return (
    <AppShell>
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <p className="text-sm font-medium text-primary">Linux fleet</p>
            <h1 className="text-2xl font-bold">Security monitoring</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Active agents, normalized authentication events, deterministic alerts, and incidents in one tenant-scoped view.
            </p>
          </div>
          <Button variant="outline" disabled={loading || eventsLoading} onClick={refreshOverview}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading || eventsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {error && (
          <Card className="border-red-300">
            <CardContent className="pt-6 text-sm text-red-700 dark:text-red-300">{error}</CardContent>
          </Card>
        )}

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard label="Events · 24h" value={overview?.totals.events ?? '—'} icon={<Activity className="h-5 w-5" />} />
          <MetricCard label="Open alerts" value={overview?.totals.open_alerts ?? '—'} icon={<AlertTriangle className="h-5 w-5" />} />
          <MetricCard label="Open incidents" value={overview?.totals.open_incidents ?? '—'} icon={<ShieldAlert className="h-5 w-5" />} />
          <MetricCard
            label="Agents online"
            value={overview ? `${overview.totals.agents - overview.totals.offline_agents}/${overview.totals.agents}` : '—'}
            icon={<Server className="h-5 w-5" />}
          />
        </div>

        <Card id="security-events" className="overflow-hidden">
          <CardHeader className="border-b bg-slate-50/70 dark:bg-slate-950/40">
            <div className="flex flex-col justify-between gap-3 md:flex-row md:items-start">
              <div>
                <div className="flex items-center gap-2">
                  <CardTitle>Event Explorer</CardTitle>
                  {eventsLoading && eventList && (
                    <Badge variant="outline" className="gap-1.5 font-normal">
                      <RefreshCw className="h-3 w-3 animate-spin" />
                      Updating
                    </Badge>
                  )}
                </div>
                <CardDescription className="mt-1">
                  Normalized Linux authentication and account activity. Raw journal and auth-log lines stay in protected evidence storage.
                </CardDescription>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />
                Acknowledged HTTPS ingestion
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <form
              className="border-b bg-white/60 p-4 dark:bg-slate-950/20"
              onSubmit={applyEventFilters}
              aria-label="Security event filters"
            >
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[0.85fr_1.15fr_0.85fr_0.85fr_1.2fr_auto] xl:items-end">
                <Select
                  label="Time range"
                  value={eventFiltersDraft.rangeSeconds}
                  onChange={(event) => setEventFiltersDraft((filters) => ({
                    ...filters,
                    rangeSeconds: event.target.value as EventFilterState['rangeSeconds'],
                  }))}
                  options={[
                    { value: '3600', label: 'Last hour' },
                    { value: '86400', label: 'Last 24 hours' },
                    { value: '604800', label: 'Last 7 days' },
                    { value: '2592000', label: 'Last 30 days' },
                  ]}
                />
                <Select
                  label="Agent"
                  value={eventFiltersDraft.agentId}
                  onChange={(event) => setEventFiltersDraft((filters) => ({
                    ...filters,
                    agentId: event.target.value,
                  }))}
                  options={[
                    { value: '', label: 'All enrolled agents' },
                    ...(overview?.agents || []).map((agent) => ({
                      value: agent.id,
                      label: `${agent.name} · ${agent.host_name}`,
                    })),
                  ]}
                />
                <Select
                  label="Severity"
                  value={eventFiltersDraft.severity}
                  onChange={(event) => setEventFiltersDraft((filters) => ({
                    ...filters,
                    severity: event.target.value as EventFilterState['severity'],
                  }))}
                  options={[
                    { value: '', label: 'All severities' },
                    { value: 'info', label: 'Info' },
                    { value: 'warning', label: 'Warning' },
                    { value: 'high', label: 'High' },
                    { value: 'critical', label: 'Critical' },
                  ]}
                />
                <Select
                  label="Outcome"
                  value={eventFiltersDraft.outcome}
                  onChange={(event) => setEventFiltersDraft((filters) => ({
                    ...filters,
                    outcome: event.target.value as EventFilterState['outcome'],
                  }))}
                  options={[
                    { value: '', label: 'All outcomes' },
                    { value: 'success', label: 'Success' },
                    { value: 'failure', label: 'Failure' },
                    { value: 'unknown', label: 'Unknown' },
                    { value: 'closed', label: 'Closed' },
                  ]}
                />
                <Input
                  label="Event type"
                  value={eventFiltersDraft.eventType}
                  onChange={(event) => setEventFiltersDraft((filters) => ({
                    ...filters,
                    eventType: event.target.value,
                  }))}
                  placeholder="ssh_auth_failure"
                  maxLength={80}
                  pattern="[a-z][a-z0-9_]{1,79}"
                  title="Use the exact lowercase event type, such as ssh_auth_failure"
                />
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1 gap-2 xl:flex-none" disabled={eventsLoading}>
                    <Filter className="h-4 w-4" />
                    Apply
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={resetEventFilters}
                    disabled={!hasActiveEventFilters && !eventFiltersDraft.eventType}
                    aria-label="Reset security event filters"
                    title="Reset filters"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </form>

            <div className="flex flex-col gap-2 border-b px-4 py-3 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
              <p aria-live="polite">
                {eventTotal > 0
                  ? `Showing ${eventStart}–${eventEnd} of ${eventTotal} normalized events`
                  : eventsLoading
                    ? 'Loading normalized events…'
                    : 'No normalized events match this view'}
              </p>
              <p>
                Window ends {formatTime(eventList?.window.to || eventWindowTo)}
                {hasActiveEventFilters ? ' · filters applied' : ''}
              </p>
            </div>

            {eventsError && (
              <div className="m-4 flex flex-col gap-3 rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200 sm:flex-row sm:items-center sm:justify-between">
                <p>{eventsError}</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setEventRefreshVersion((version) => version + 1)}
                >
                  Retry
                </Button>
              </div>
            )}

            {eventsLoading && !eventList && (
              <div className="space-y-3 p-4" aria-label="Loading security events">
                {[0, 1, 2].map((row) => (
                  <div key={row} className="h-20 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-900" />
                ))}
              </div>
            )}

            {!eventsLoading && !eventsError && eventList?.events.length === 0 && (
              <div className="px-6 py-12 text-center">
                <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-900">
                  <Activity className="h-5 w-5" />
                </div>
                <p className="mt-4 font-medium">No events in this view</p>
                <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                  Widen the time range or reset filters. New authentication activity appears after the agent sends its next acknowledged batch.
                </p>
                {hasActiveEventFilters && (
                  <Button type="button" variant="outline" size="sm" className="mt-4" onClick={resetEventFilters}>
                    Reset filters
                  </Button>
                )}
              </div>
            )}

            {eventList && eventList.events.length > 0 && (
              <>
                <div className="hidden lg:block" aria-busy={eventsLoading}>
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="w-[190px]">Observed</TableHead>
                        <TableHead>Event</TableHead>
                        <TableHead className="w-[180px]">Actor</TableHead>
                        <TableHead className="w-[220px]">Origin</TableHead>
                        <TableHead className="w-[120px]">Outcome</TableHead>
                        <TableHead className="w-[120px]">Severity</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {eventList.events.map((event) => (
                        <TableRow key={event.id} className={eventsLoading ? 'opacity-60' : undefined}>
                          <TableCell className={`border-l-4 ${severityRailClass(event.severity)}`}>
                            <time
                              className="font-mono text-xs tabular-nums"
                              dateTime={new Date(event.timestamp * 1_000).toISOString()}
                            >
                              {formatTime(event.timestamp)}
                            </time>
                            <p className="mt-1 text-[11px] text-muted-foreground">
                              received {formatTime(event.received_at)}
                            </p>
                          </TableCell>
                          <TableCell>
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-medium">{humanizeEventType(event.event_type)}</p>
                                {event.flagged && <Badge variant="red">Flagged</Badge>}
                              </div>
                              <p className="mt-1 font-mono text-[11px] text-muted-foreground">{event.event_type}</p>
                              {(event.process || event.command) && (
                                <p
                                  className="mt-2 max-w-xl truncate font-mono text-xs text-slate-600 dark:text-slate-300"
                                  title={event.command || event.process || undefined}
                                >
                                  {event.process || 'command'}
                                  {event.pid !== null ? ` [${event.pid}]` : ''}
                                  {event.command ? ` · ${event.command}` : ''}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm">{event.user_name || 'System'}</p>
                            {event.target_user && event.target_user !== event.user_name && (
                              <p className="mt-1 text-xs text-muted-foreground">target {event.target_user}</p>
                            )}
                            {event.auth_method && (
                              <p className="mt-1 text-xs text-muted-foreground">{event.auth_method}</p>
                            )}
                          </TableCell>
                          <TableCell>
                            <p className="font-mono text-xs">
                              {event.source_ip || event.host}
                              {event.source_ip && event.source_port ? `:${event.source_port}` : ''}
                            </p>
                            <p className="mt-1 truncate text-xs text-muted-foreground" title={`${event.host} · ${event.source}`}>
                              {event.host} · {event.source}
                            </p>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={outcomeBadgeClass(event.outcome)}>
                              {event.outcome}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={severityBadgeClass(event.severity)}>
                              {event.severity}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="divide-y lg:hidden" aria-busy={eventsLoading}>
                  {eventList.events.map((event) => (
                    <article
                      key={event.id}
                      className={`border-l-4 p-4 ${severityRailClass(event.severity)} ${eventsLoading ? 'opacity-60' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-medium">{humanizeEventType(event.event_type)}</h3>
                            {event.flagged && <Badge variant="red">Flagged</Badge>}
                          </div>
                          <p className="mt-1 font-mono text-[11px] text-muted-foreground">{event.event_type}</p>
                        </div>
                        <Badge variant="outline" className={severityBadgeClass(event.severity)}>
                          {event.severity}
                        </Badge>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <p className="text-muted-foreground">Observed</p>
                          <time className="mt-1 block font-mono" dateTime={new Date(event.timestamp * 1_000).toISOString()}>
                            {formatTime(event.timestamp)}
                          </time>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Outcome</p>
                          <Badge variant="outline" className={`mt-1 ${outcomeBadgeClass(event.outcome)}`}>
                            {event.outcome}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Actor</p>
                          <p className="mt-1">{event.user_name || 'System'}{event.target_user ? ` → ${event.target_user}` : ''}</p>
                        </div>
                        <div className="min-w-0">
                          <p className="text-muted-foreground">Origin</p>
                          <p className="mt-1 truncate font-mono">{event.source_ip || event.host}</p>
                        </div>
                      </div>
                      {(event.process || event.command) && (
                        <p className="mt-3 break-words rounded-lg bg-slate-50 px-3 py-2 font-mono text-xs text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                          {event.process || 'command'}
                          {event.pid !== null ? ` [${event.pid}]` : ''}
                          {event.command ? ` · ${event.command}` : ''}
                        </p>
                      )}
                      <p className="mt-3 text-xs text-muted-foreground">{event.host} · {event.source}</p>
                    </article>
                  ))}
                </div>
              </>
            )}

            {eventList && eventList.pagination.total_pages > 1 && (
              <div className="border-t px-4 py-4">
                <Pagination
                  currentPage={eventList.pagination.page}
                  totalPages={eventList.pagination.total_pages}
                  onPageChange={setEventPage}
                />
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-6 xl:grid-cols-[1.4fr_1fr]">
          <Card>
            <CardHeader>
              <CardTitle>Recent alerts</CardTitle>
              <CardDescription>Native detections from server-scrubbed, persisted agent batches.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {loading && !overview && <p className="text-sm text-muted-foreground">Loading alerts…</p>}
              {overview?.recent_alerts.length === 0 && <p className="text-sm text-muted-foreground">No alerts in the selected 24-hour window.</p>}
              {overview?.recent_alerts.map((alert) => (
                <div key={alert.id} className={`rounded-xl border p-4 ${severityClass(alert.severity)}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-semibold">{alert.title}</p>
                    <span className="text-xs uppercase tracking-wide">{alert.status}</span>
                  </div>
                  <p className="mt-2 text-sm opacity-80">
                    {alert.event_count} event{alert.event_count === 1 ? '' : 's'}
                    {alert.source_ip ? ` · ${alert.source_ip}` : ''}
                    {' · '}{formatTime(alert.last_seen_at)}
                  </p>
                  {alert.incident_id && (
                    <Link
                      to="/security/incidents/$incidentId"
                      params={{ incidentId: alert.incident_id }}
                      className="mt-3 inline-flex text-sm font-medium underline underline-offset-4"
                    >
                      Review incident
                    </Link>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Enrolled agents</CardTitle>
              <CardDescription>A host is marked offline after five minutes without an accepted batch.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {loading && !overview && <p className="text-sm text-muted-foreground">Loading agents…</p>}
              {overview?.agents.length === 0 && (
                <div className="rounded-xl border border-dashed p-5 text-sm text-muted-foreground">
                  No Linux agents are enrolled. Install a Debian package and run the device-code login.
                </div>
              )}
              {overview?.agents.map((agent) => (
                <div key={agent.id} className="flex items-start justify-between gap-3 rounded-xl border p-4">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{agent.name}</p>
                    <p className="truncate text-sm text-muted-foreground">{agent.host_name}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {agent.platform}/{agent.architecture} · {agent.agent_version}
                    </p>
                  </div>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
                    agent.offline
                      ? 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'
                      : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'
                  }`}>
                    <ShieldCheck className="h-3.5 w-3.5" />
                    {agent.offline ? 'offline' : 'online'}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
};

const MetricCard: React.FC<{ label: string; value: string | number; icon: React.ReactNode }> = ({
  label,
  value,
  icon,
}) => (
  <Card>
    <CardContent className="flex items-center justify-between gap-4 pt-6">
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="mt-1 text-2xl font-bold">{value}</p>
      </div>
      <div className="rounded-xl bg-primary/10 p-3 text-primary">{icon}</div>
    </CardContent>
  </Card>
);
